import React, { useEffect } from 'react';
import { Box, IconButton, Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TitleIcon from '@mui/icons-material/Title';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { editorSourceToHtml, htmlToMarkdown } from './promptFormat';

interface PromptRichEditorProps {
  value: string;
  sourceKey: string;
  onChange: (markdown: string) => void;
  editable: boolean;
}

const proseSx = {
  '& .ProseMirror': {
    minHeight: 220,
    maxHeight: 420,
    overflowY: 'auto',
    px: 2.5,
    py: 2,
    outline: 'none',
    fontSize: '0.95rem',
    lineHeight: 1.65,
    color: '#1c2b3a',
    '& p': { margin: '0.45em 0' },
    '& ul, & ol': { paddingLeft: '1.4em', margin: '0.55em 0' },
    '& li': { margin: '0.2em 0' },
    '& h1, & h2, & h3': {
      margin: '0.9em 0 0.4em',
      color: '#072f53',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    '& h2': { fontSize: '1.15rem' },
    '& h3': { fontSize: '1.05rem' },
    '& strong': { fontWeight: 700 },
  },
  '& .ProseMirror p.is-editor-empty:first-child::before': {
    color: '#94a3b8',
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
};

const PromptRichEditor: React.FC<PromptRichEditorProps> = ({ value, sourceKey, onChange, editable }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Placeholder.configure({
        placeholder: 'Skriv reglerna med rubriker, fetstil och punktlistor så att de blir lätta att läsa.',
      }),
    ],
    content: editorSourceToHtml(value),
    editable,
    onUpdate: ({ editor: next }) => {
      onChange(htmlToMarkdown(next.getHTML()));
    },
  }, [sourceKey]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        overflow: 'hidden',
        borderColor: 'rgba(7,47,83,0.14)',
        borderRadius: 2,
        bgcolor: '#fff',
        ...proseSx,
      }}
    >
      {editable && (
        <Box
          sx={{
            px: 1,
            py: 0.75,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            bgcolor: '#f7f9fc',
            borderBottom: '1px solid rgba(7,47,83,0.08)',
          }}
        >
          <ToggleButtonGroup size="small" exclusive={false}>
            <ToggleButton value="bold" selected={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="italic" selected={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="underline" selected={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small">
            <ToggleButton value="h2" selected={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
              <TitleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="bullet" selected={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <FormatListBulletedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="ordered" selected={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Ångra">
            <span>
              <IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Gör om">
            <span>
              <IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      {!editable && (
        <Box sx={{ px: 2, pt: 1.25 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.3 }}>
            Endast visning
          </Typography>
        </Box>
      )}
      <EditorContent editor={editor} />
    </Paper>
  );
};

export default PromptRichEditor;
